import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { currencyFormatter, getExportPrefix } from '../../../../../shared/components/table/functions';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import { doFetch } from '../../../../../helpers';

function MetaCatalogMgr() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [agGrid, setAgGrid] = useState(null);

  // Cursor cache for Meta's pagination (offset -> cursor mapping)
  const cursorCacheRef = useRef({ 0: null });

  const pageSize = 100;

  const exportPrefix = getExportPrefix('meta-catalog', selectedSeller, true);

  const columnDefs = [
    {
      field: 'image_url',
      headerName: 'Image',
      width: 100,
      cellRenderer: (params) => {
        if (params.data?.image_url) {
          return (
            <img
              src={params.data.image_url}
              alt="Product"
              style={{ maxWidth: '75px', maxHeight: '75px', objectFit: 'contain' }}
            />
          );
        }
        return '';
      },
    },
    {
      field: 'product_id',
      headerName: 'Product ID',
      cellRenderer: (params) => {
        if (params.data?.landing_page_url) {
          return (
            <a
              href={params.data.landing_page_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {params.data.product_id}
            </a>
          );
        }
        return params.data?.product_id || '';
      },
    },
    {
      field: 'sku',
      headerName: 'SKU',
    },
    { field: 'product_name', headerName: 'Product Name' },
    { field: 'description', headerName: 'Description' },
    {
      field: 'price',
      headerName: 'Price',
      valueFormatter: currencyFormatter,
    },
    {
      field: 'sale_price',
      headerName: 'Sale Price',
      valueFormatter: currencyFormatter,
    },
    {
      field: 'ad_status',
      headerName: 'Ad Status',
    },
    {
      field: 'reporting_visibility',
      headerName: 'Reporting Visibility',
    },
    {
      field: 'brand',
      headerName: 'Brand',
    },
    {
      field: 'product_type',
      headerName: 'Product Type',
    },
    {
      field: 'google_product_category',
      headerName: 'Category',
    },
    {
      field: 'size',
      headerName: 'Size',
    },
    {
      field: 'color',
      headerName: 'Color',
    },
    {
      field: 'availability',
      headerName: 'Availability',
    },
    {
      field: 'condition',
      headerName: 'Condition',
    },
    {
      field: 'inventory',
      headerName: 'Inventory',
    },
    {
      field: 'gtin',
      headerName: 'GTIN',
    },
    {
      field: 'mpn',
      headerName: 'MPN',
    },
    {
      field: 'status',
      headerName: 'Status',
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    cellStyle: (params) => {
      if (params && params.value < 0.00) {
        return { color: 'red' };
      }
      return null;
    },
  };

  const tableConfig = {
    showDatePicker: false,
    showFooter: false,
    parentControlledLoad: true,
    apiEndPoint: `${config.serverUrl}/meta/catalogs/${selectedSeller.value}`,
    paginationPageSize: pageSize,
    cacheBlockSize: pageSize,
  };

  // Helper function to fetch a specific page with cursor
  const fetchMetaPage = async (cursor, headerWithAuth) => {
    const url = `${tableConfig.apiEndPoint}?limit=${pageSize}${cursor ? `&after=${cursor}` : ''}`;
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };
    return doFetch(url, requestOptions);
  };

  const datasource = {
    getRows: async (params) => {
      const { request, success, fail } = params;
      const { startRow } = request;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      const offset = startRow || 0;

      try {
        // Get cursor for this page
        const cursor = cursorCacheRef.current[offset];

        // Only forward navigation is supported (cursor is cached from previous page)
        if (offset > 0 && cursor === undefined) {
          console.warn('Meta: Missing cursor for offset', offset);
          fail();
          return;
        }

        const data = await fetchMetaPage(cursor, headerWithAuth);

        // Cache next cursor if available
        if (data.paging?.hasNext && data.paging?.cursors?.after) {
          cursorCacheRef.current[offset + pageSize] = data.paging.cursors.after;
        }

        // Use total count from API if available to avoid infinite scroll loops
        const rowCount = data.total || (data.results ? data.results.length : 0);

        success({
          rowData: data.results || [],
          rowCount,
        });
      } catch (error) {
        console.error('Meta catalog fetch error:', error);
        fail();
      }
    },
  };

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  };

  useEffect(() => {
    if (agGrid) {
      cursorCacheRef.current = { 0: null };
      agGrid.refreshData();
    }
  }, [selectedSeller.value, agGrid]);

  return (
    <Container className="todo-app">
      <Row>
        <CardPanel
          title="Meta Catalog"
          subhead={new Date().toISOString()}
          autorenewButton={false}
          closeButton={false}
        >
          <TableBuilder
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            apiEndPoint={tableConfig.apiEndPoint}
            showDatePicker={tableConfig.showDatePicker}
            showFooter={tableConfig.showFooter}
            rowHeight={75}
            pagination
            rowModelType="serverSide"
            datasource={datasource}
            paginationPageSize={tableConfig.paginationPageSize}
            cacheBlockSize={tableConfig.cacheBlockSize}
            serverSideInfiniteScroll={false}
            parentControlledLoad={tableConfig.parentControlledLoad}
            editSelectedRowsButton={false}
            showImportButton={false}
            parentAccess={parentAccess}
            exportPrefix={exportPrefix}
          />
        </CardPanel>
      </Row>
    </Container>
  );
}

export default MetaCatalogMgr;
